/**
 * Owns the single `useConversation` call for a request and renders `NotificationsPanel`
 * together with its layout spacer `PanelReservedSpace` under one shared visibility condition.
 *
 * This is the single source of truth for "is the chat panel visible" — both the panel and the
 * spacer that reserves scroll-flow space for it derive from the same `isPanelVisible` value, so
 * they can never disagree (previously the page-level render condition (`model?.id`) was broader
 * than the panel's own internal visibility check, reserving dead space and causing a layout
 * jump when a request has no conversation, e.g. DRAFT/CREATED).
 *
 * The conversation load is triggered here (on mount and whenever `requestId` changes) instead of
 * inside `NotificationsPanel`, since visibility depends on `hasLoaded`/`conversation`/`loadError`
 * — deciding visibility requires the same state that triggers the load, so both must live in the
 * same place to avoid a duplicated `useConversation` call (which would double the API request).
 *
 * The spacer height is derived from the live collapsed-panel header height, measured via a
 * `ResizeObserver` on the panel's header row. A previously hard-coded height was too small and
 * left the bottom-most quote-card button only a few pixels clear of the panel; measuring the
 * real header height makes the reserved space exact regardless of theme, font or translations.
 */

import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useSnackbarEx} from "@/lib/snackbarex";
import {NotificationI18N} from "@/translations";
import {useConversation} from "./state/useConversation";
import NotificationsPanel from "./NotificationsPanel";
import PanelReservedSpace, {
    COLLAPSED_PANEL_RESERVED_HEIGHT_PX,
    PANEL_BOTTOM_OFFSET_PX,
    PANEL_SPACER_BUFFER_PX
} from "./PanelReservedSpace";

interface NotificationsWithPanelProps {
    requestId: number;
}

/**
 * Renders the chat panel and its reserved-space spacer for the given request, or nothing at
 * all while the conversation has not loaded yet, or once loaded there is neither a conversation
 * nor a load error (DRAFT/CREATED requests and closed legacy requests without a conversation).
 */
const NotificationsWithPanel: React.FC<NotificationsWithPanelProps> = ({requestId}) => {
    const {t} = useTranslation();
    const {showMessage} = useSnackbarEx();
    const {conversation, load, isLoading, loadError, hasLoaded, sendMessage, markRead} = useConversation();

    const headerRef = useRef<HTMLDivElement | null>(null);
    const [reservedHeight, setReservedHeight] = useState<number>(COLLAPSED_PANEL_RESERVED_HEIGHT_PX);

    const loadWithErrorHandling = useCallback(() => {
        load(requestId).catch((error: unknown) => {
            showMessage({
                severity: "error",
                summary: t(NotificationI18N.chat.window.loadFailed),
                error
            });
        });
    }, [load, requestId, showMessage, t]);

    // Load conversation on mount and when requestId changes
    useEffect(() => {
        loadWithErrorHandling();
    }, [loadWithErrorHandling]);

    // Hide the panel entirely until the first load has settled (prevents a flash of the panel
    // header), and keep it hidden when loaded with neither a conversation nor an error (DRAFT/
    // CREATED requests, closed legacy requests without a conversation). The spacer shares this
    // exact condition, so it never reserves space for a panel that is not actually visible.
    const isPanelVisible = hasLoaded && (conversation !== undefined || loadError !== undefined);

    // Measure the collapsed panel header once it is visible and reserve exactly its footprint
    // (header height + bottom offset + buffer). The header row height is stable across the
    // panel's open/closed state, so this reflects the collapsed footprint even while expanded.
    useLayoutEffect(() => {
        const header = headerRef.current;
        if (!isPanelVisible || header === null) {
            return;
        }
        const applyHeight = () => {
            setReservedHeight(header.offsetHeight + PANEL_BOTTOM_OFFSET_PX + PANEL_SPACER_BUFFER_PX);
        };
        applyHeight();
        const observer = new ResizeObserver(applyHeight);
        observer.observe(header);
        return () => observer.disconnect();
    }, [isPanelVisible]);

    if (!isPanelVisible) {
        return null;
    }

    // Order matters: the spacer must come BEFORE the panel. The panel's sticky wrapper is a
    // `height: 0` flow element, so its static anchor sits at its DOM position. Rendering the
    // spacer first places that anchor `reservedHeight` px below the last real content (the quote
    // card button), so once scrolled to the bottom the panel settles below the button instead of
    // locking onto it. With the spacer after the panel, the panel would anchor at the button's
    // level and overlap it at every scroll position, no matter how much space is reserved.
    return (
            <>
                <PanelReservedSpace height={reservedHeight}/>
                <NotificationsPanel
                        requestId={requestId}
                        conversation={conversation}
                        isLoading={isLoading}
                        sendMessage={sendMessage}
                        markRead={markRead}
                        onRefresh={loadWithErrorHandling}
                        headerRef={headerRef}
                />
            </>
    );
};

export default NotificationsWithPanel;
